// Seed alerts for the Intelligence Board. A mix of:
//  - red    (action required now)
//  - gold   (heads-up — may need action soon)
//  - grey   (FYI / informational)
//
// Predictive alerts are tagged isPredictive=true so the panel can render
// them with the "AI"/sparkle treatment that makes the magic visible.
//
// Each alert references a real entity (driver, flight, or gate) by id so
// clicking the alert cross-links into the map and comms panels.

import { DRIVERS, GATE_STATE } from './mockLiveState';

// Pull a few real entities so cross-linking works against actual IDs.
const inRunDriver  = DRIVERS.find(d => d.status === 'in_run');
const idleDriver   = DRIVERS.find(d => d.status === 'on_shift');
const idleDriver2  = DRIVERS.filter(d => d.status === 'on_shift')[3];
const lunchDriver  = DRIVERS.find(d => d.status === 'on_lunch');
const breakDriver  = DRIVERS.find(d => d.status === 'on_break');
const atGateDriver = DRIVERS.find(d => d.status === 'at_gate');

// Find a flight at a parked gate to reference
let flightAtGate, flightGateId;
for (const [gid, s] of GATE_STATE.entries()) {
  if (s.state === 'parked' && s.flightNumber) {
    flightAtGate = s.flightNumber;
    flightGateId = gid;
    break;
  }
}
let secondFlight, secondGateId;
let count = 0;
for (const [gid, s] of GATE_STATE.entries()) {
  if (s.state === 'parked' && s.flightNumber && gid !== flightGateId) {
    if (count++ === 2) {
      secondFlight = s.flightNumber;
      secondGateId = gid;
      break;
    }
  }
}

export const ALERTS = [
  // ───── RED — action required ─────
  {
    id: 'alert-1',
    severity: 'red',
    title: 'No driver assigned',
    body: `Inbound ${flightAtGate} (gate ${flightGateId}) lands in 12 min — no run on the board.`,
    entity: { kind: 'flight', id: flightAtGate },
    age: '2m',
    actions: ['Auto-assign', 'Snooze 5m'],
  },
  {
    id: 'alert-2',
    severity: 'red',
    title: 'Driver late to gate',
    body: `${atGateDriver?.name} (badge ${atGateDriver?.badge}) marked en route 8 min ago — should have arrived.`,
    entity: { kind: 'driver', id: atGateDriver?.id },
    age: '4m',
    actions: ['Radio driver', 'Reassign'],
  },
  {
    id: 'alert-3',
    severity: 'red',
    title: 'Hot bag projected miss',
    body: `WN 1854 → WN 2237 connection: bag transfer pace will miss by ~7 min at gate ${flightGateId}.`,
    entity: { kind: 'gate', id: flightGateId },
    age: '1m',
    actions: ['Reassign driver', 'Acknowledge'],
    isPredictive: true,
  },

  // ───── GOLD — heads up ─────
  {
    id: 'alert-4',
    severity: 'gold',
    title: 'Driver imbalance — sector 4',
    body: `6 drivers idle in sector 4, 2 active runs in sector 2. Recommend pulling 2 west.`,
    entity: null,
    age: '6m',
    actions: ['Show on map', 'Acknowledge'],
    isPredictive: true,
  },
  {
    id: 'alert-5',
    severity: 'gold',
    title: 'Gate change',
    body: `${secondFlight} reassigned from C${28} → ${secondGateId}. Driver thread updated.`,
    entity: { kind: 'flight', id: secondFlight },
    age: '8m',
    actions: ['Notify driver', 'Acknowledge'],
  },
  {
    id: 'alert-6',
    severity: 'gold',
    title: 'Lunch window opening',
    body: `Bank lull window 09:30 – 10:15. Capacity for ~14 drivers on lunch.`,
    entity: null,
    age: '11m',
    actions: ['Open lunch picker', 'Acknowledge'],
    isPredictive: true,
  },
  {
    id: 'alert-7',
    severity: 'gold',
    title: 'Tug down',
    body: `Tug 4127 reported brake issue at gate C42. ${idleDriver?.name} reassigned to spare.`,
    entity: { kind: 'gate', id: 'C42' },
    age: '14m',
    actions: ['Acknowledge'],
  },

  // ───── GREY — FYI ─────
  {
    id: 'alert-8',
    severity: 'grey',
    title: 'Download stalled',
    body: `Inbound bin scan from gate C${37} hasn't progressed in 4 min. Verify with ramp.`,
    entity: { kind: 'gate', id: 'C37' },
    age: '4m',
    actions: ['Mark resolved'],
  },
  {
    id: 'alert-9',
    severity: 'grey',
    title: 'Driver returning from lunch',
    body: `${lunchDriver?.name} returns at 08:00. Available for next bank.`,
    entity: { kind: 'driver', id: lunchDriver?.id },
    age: '15m',
    actions: ['Acknowledge'],
  },
  {
    id: 'alert-10',
    severity: 'grey',
    title: 'Weather watch',
    body: `Light rain forecast 09:00 – 11:00. No ground stops expected.`,
    entity: null,
    age: '20m',
    actions: ['Acknowledge'],
  },
  {
    id: 'alert-11',
    severity: 'grey',
    title: 'Driver on break',
    body: `${breakDriver?.name} on 15-min break. Returns 07:55.`,
    entity: { kind: 'driver', id: breakDriver?.id },
    age: '8m',
    actions: ['Acknowledge'],
  },
];

export const SEVERITY_META = {
  red:  { label: 'Action',   color: '#FF0000', bg: 'rgba(255, 0, 0, 0.10)',  border: 'rgba(255, 0, 0, 0.35)'  },
  gold: { label: 'Heads-up', color: '#F9B612', bg: 'rgba(249, 182, 18, 0.10)', border: 'rgba(249, 182, 18, 0.35)' },
  grey: { label: 'FYI',      color: '#94A3B8', bg: 'rgba(148, 163, 184, 0.08)', border: 'rgba(148, 163, 184, 0.25)' },
};
